import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { MultiSelect } from '@/components/ui/multi-select';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ScrollPage } from '@/components/layout/PageContainer';
import { useGroupsList } from '@/features/groups/api';
import { useLocationsList } from '@/features/locations/api';
import { useTrunksList } from '@/features/trunks/api';
import {
  useDialerDetail,
  usePatchDialerGroup,
  useUpdateDialer,
} from '../api';
import { buildGroupRowsPayload } from '../payload';
import { dialerUpdateSchema, type DialerUpdateFormInput } from '../schema';
import type { DialerGroupRef } from '../types';

export function UpdateDialerPage() {
  const navigate = useNavigate();
  const { dialerId: dialerIdParam } = useParams<{ dialerId: string }>();
  const dialerId = Number(dialerIdParam);

  const detail = useDialerDetail(dialerId);
  const trunks = useTrunksList();
  const locations = useLocationsList();
  const groups = useGroupsList();
  const patchGroup = usePatchDialerGroup();
  const update = useUpdateDialer();

  const [groupRows, setGroupRows] = useState<DialerGroupRef[]>([]);
  const [pendingRemove, setPendingRemove] = useState<DialerGroupRef | null>(null);

  const groupOptions = useMemo(
    () =>
      (groups.data ?? []).map((g) => ({
        value: String(g.groupId),
        label: g.groupName,
      })),
    [groups.data],
  );

  const groupValue = useMemo(
    () => groupRows.map((g) => String(g.groupId)),
    [groupRows],
  );

  const form = useForm<DialerUpdateFormInput>({
    resolver: zodResolver(dialerUpdateSchema),
    mode: 'onTouched',
    defaultValues: {
      dialerName: '',
      dialerIP: '',
      dialerPort: '',
      trunkId: '',
      locationId: '',
      channelReduction: '',
      cpsReduction: '',
    },
  });

  useEffect(() => {
    if (!detail.data) return;
    const d = detail.data.dialerObj;
    form.reset({
      dialerName: d.dialerName ?? '',
      dialerIP: d.dialerIP ?? '',
      dialerPort: d.dialerPort != null ? String(d.dialerPort) : '',
      trunkId: d.trunkId != null ? String(d.trunkId) : '',
      locationId: d.locationId != null ? String(d.locationId) : '',
      channelReduction:
        d.channelReduction != null ? String(d.channelReduction) : '',
      cpsReduction: d.cpsReduction != null ? String(d.cpsReduction) : '',
    });
    setGroupRows(detail.data.groups ?? []);
  }, [detail.data, form]);

  const baseGroupPayload = () => {
    const v = form.getValues();
    return {
      dialerId,
      dialerName: v.dialerName.trim(),
      dialerIP: v.dialerIP.trim(),
      dialerPort: v.dialerPort.trim(),
      cpsReduction: v.cpsReduction ?? '',
      channelReduction: v.channelReduction ?? '',
      trunkId: v.trunkId,
      locationId: v.locationId,
    };
  };

  /**
   * Drive add/remove off MultiSelect changes. Adds save immediately;
   * removes open the confirm dialog so the user can back out. The
   * MultiSelect stays controlled by `groupValue`, so cancelling a remove
   * naturally snaps the chip back without extra wiring.
   */
  const handleGroupsChange = (next: string[]) => {
    const current = groupValue;
    const added = next.filter((id) => !current.includes(id));
    const removed = current.find((id) => !next.includes(id));

    for (const id of added) {
      const picked = (groups.data ?? []).find(
        (g) => String(g.groupId) === id,
      );
      if (!picked) continue;
      patchGroup.mutate(
        {
          ...baseGroupPayload(),
          groupId: String(picked.groupId),
          groupName: picked.groupName,
          operationType: 'add',
        },
        {
          onSuccess: (res) => {
            toast.success(res?.message ?? `Added "${picked.groupName}"`);
            setGroupRows((prev) =>
              prev.some((r) => String(r.groupId) === String(picked.groupId))
                ? prev
                : [...prev, { groupId: picked.groupId, groupName: picked.groupName }],
            );
          },
          onError: (err) =>
            toast.error(readErrorMessage(err) ?? 'Could not add the group.'),
        },
      );
    }

    if (removed) {
      const target = groupRows.find((g) => String(g.groupId) === removed);
      if (target) setPendingRemove(target);
    }
  };

  const confirmRemoveGroup = () => {
    if (!pendingRemove) return;
    const target = pendingRemove;
    patchGroup.mutate(
      {
        ...baseGroupPayload(),
        groupId: String(target.groupId),
        groupName: target.groupName,
        operationType: 'remove',
      },
      {
        onSuccess: (res) => {
          toast.success(res?.message ?? `Removed "${target.groupName}"`);
          setGroupRows((prev) =>
            prev.filter((r) => String(r.groupId) !== String(target.groupId)),
          );
          setPendingRemove(null);
        },
        onError: (err) =>
          toast.error(readErrorMessage(err) ?? 'Could not remove the group.'),
      },
    );
  };

  const onSubmit = (input: DialerUpdateFormInput) => {
    if (groupRows.length === 0) {
      toast.error('A dialer must belong to at least one group.');
      return;
    }
    const options = groupRows.map((g) => ({
      value: String(g.groupId),
      label: g.groupName,
    }));
    const groupIds = groupRows.map((g) => String(g.groupId));

    update.mutate(
      {
        dialerId,
        dialerName: input.dialerName.trim(),
        dialerIP: input.dialerIP.trim(),
        dialerPort: input.dialerPort.trim(),
        cpsReduction: input.cpsReduction ?? '',
        channelReduction: input.channelReduction ?? '',
        trunkId: input.trunkId,
        locationId: input.locationId,
        groupRows: buildGroupRowsPayload(groupIds, options),
      },
      {
        onSuccess: (res) => {
          toast.success(res?.message ?? 'Dialer updated');
          navigate('/dialer-list');
        },
        onError: (err) =>
          toast.error(readErrorMessage(err) ?? 'Could not update the dialer.'),
      },
    );
  };

  return (
    <ScrollPage>
      <PageHeader
        title="Update Dialer"
        description={
          detail.data
            ? `Editing "${detail.data.dialerObj.dialerName}".`
            : 'Loading dialer details…'
        }
        actions={
          <Button asChild variant="outline">
            <Link to="/dialer-list">
              <ArrowLeft className="h-4 w-4" />
              Back to dialers
            </Link>
          </Button>
        }
      />

      <Form {...form}>
        <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)} noValidate>
          <Card>
            <CardHeader>
              <CardTitle>Dialer details</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <FormField
                control={form.control}
                name="dialerName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Dialer name</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="dialerIP"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>IP / Host</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="dialerPort"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Port</FormLabel>
                    <FormControl>
                      <Input {...field} inputMode="numeric" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="channelReduction"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Channel reduction</FormLabel>
                    <FormControl>
                      <Input {...field} inputMode="numeric" placeholder="0" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="cpsReduction"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>CPS reduction</FormLabel>
                    <FormControl>
                      <Input {...field} inputMode="numeric" placeholder="0" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Groups</CardTitle>
              <CardDescription>
                Groups attach and detach immediately — saved as you toggle.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <label className="text-sm font-medium leading-none">Groups</label>
              <MultiSelect
                options={groupOptions}
                value={groupValue}
                onChange={handleGroupsChange}
                placeholder={
                  groups.isLoading ? 'Loading groups…' : 'Pick groups for this dialer'
                }
                searchPlaceholder="Filter groups…"
                disabled={patchGroup.isPending}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Routing</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="trunkId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Trunk</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue
                            placeholder={
                              trunks.isLoading ? 'Loading trunks…' : 'Select a trunk'
                            }
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {(trunks.data ?? []).map((t) => (
                          <SelectItem key={t.trunkId} value={String(t.trunkId)}>
                            {t.trunkName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="locationId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Location</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue
                            placeholder={
                              locations.isLoading
                                ? 'Loading locations…'
                                : 'Select a location'
                            }
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {(locations.data ?? []).map((l) => (
                          <SelectItem key={l.locationId} value={String(l.locationId)}>
                            {l.locationName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <div className="flex items-center justify-end gap-2">
            <Button asChild variant="ghost">
              <Link to="/dialer-list">Cancel</Link>
            </Button>
            <Button type="submit" disabled={update.isPending}>
              {update.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Save changes
            </Button>
          </div>
        </form>
      </Form>

      <ConfirmDialog
        open={pendingRemove !== null}
        onOpenChange={(o) => !o && setPendingRemove(null)}
        title="Are you sure you want to delete?"
        description={
          <>
            <span className="font-medium text-foreground">
              {pendingRemove?.groupName}
            </span>{' '}
            will be detached from this dialer.
          </>
        }
        confirmLabel="Remove"
        tone="destructive"
        loading={patchGroup.isPending}
        onConfirm={confirmRemoveGroup}
      />
    </ScrollPage>
  );
}

function readErrorMessage(err: unknown): string | undefined {
  if (err && typeof err === 'object') {
    const a = err as { response?: { data?: { message?: string } }; message?: string };
    return a.response?.data?.message ?? a.message;
  }
  return undefined;
}
